import { useState, useMemo } from 'react'
import { Outlet, useNavigation, useLocation } from 'react-router-dom'
import { NotificationsProvider } from '@toolpad/core/useNotifications'
import { ThemeProvider } from '@mui/material/styles'
import { useColorScheme } from '@mui/material/styles'
import { AppProvider } from '@toolpad/core/react-router-dom'
import { DashboardLayout, ThemeSwitcher } from '@toolpad/core/DashboardLayout'
import { PageContainer } from '@toolpad/core/PageContainer'
import { Account } from '@toolpad/core/Account'
import { AccountPopoverFooter } from '@toolpad/core/Account'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'
import { AuthStore } from 'store/AuthStore'
import { UserStore } from 'store/UserStore'
// import Notification from 'components/Notification/Notification'
import SkeletonContent from 'components/SkeletonContent/SkeletonContent'
import {
  Typography,
  useMediaQuery,
  Button,
  Avatar,
  Divider,
  Stack,
  Box,
} from '@mui/material'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { ToastContainer } from 'react-toastify'

import navigation from './navigation'
import theme from 'theme'

const Layout = () => {
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'))
  const { location: navLocation, state } = useNavigation()
  const location = useLocation()
  const isNavigating = useMemo(
    () => state === 'loading' && location.pathname !== navLocation.pathname,
    [state, location, navLocation]
  )

  const ToolbarActions = () => (
    <Stack direction="row" spacing={0}>
      <ThemeSwitcher />
      {/* <Notification /> */}
    </Stack>
  )

  const ConditionalAccount = () => {
    return isLargeScreen ? (
      <Account
        localeText={{ signOutLabel: 'Выйти' }}
        slotProps={{
          signOutButton: { variant: 'contained' },
          preview: {
            variant: 'condensed',
            slotProps: {
              avatar: {
                sx: {
                  width: '5.2rem',
                  height: '5.2rem',
                },
              },
            },
          },
        }}
      />
    ) : null
  }

  const Logo = () => {
    const { colorScheme = 'light' } = useColorScheme()

    return (
      <Box sx={{ ml: isLargeScreen ? 0 : 'auto', height: '100%' }}>
        <img
          src={`/logo-${colorScheme}-theme.svg`}
          height="100%"
          width={isLargeScreen ? 'auto' : 200}
          alt="Цифровые двойники"
        />
      </Box>
    )
  }

  const branding = {
    logo: <Logo />,
    title: '',
  }

  const [session, setSession] = useState({
    user: {
      name: UserStore.data.name,
      email: UserStore.data.email,
      image: UserStore.data.image ?? '',
    },
  })

  const authentication = useMemo(() => {
    return {
      signIn: () => {
        setSession({
          user: {
            name: UserStore.data.name,
            email: UserStore.data.email,
            image: UserStore.data.image,
          },
        })
      },
      signOut: () => {
        AuthStore.logout()
      },
    }
  }, [])

  function SidebarFooter({ mini }) {
    return (
      <>
        {mini || isLargeScreen ? null : (
          <Stack direction="column" sx={{ width: '100%' }}>
            <Stack direction="row" sx={{ alignItems: 'top', ml: 1, mb: 1 }}>
              <Avatar
                sx={{
                  width: 45,
                  height: 45,
                }}
                src={UserStore.data.image ?? ''}
                alt={UserStore.data.name ?? ''}
              >
                <PersonIcon />
              </Avatar>

              <Stack sx={{ ml: 1, gap: 1 }}>
                <Typography variant="buttonLg">
                  {UserStore.data.name}
                </Typography>
                <Typography variant="caption">
                  {UserStore.data.email}
                </Typography>
              </Stack>
            </Stack>

            <Divider />
            <AccountPopoverFooter sx={{ justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={authentication.signOut}
                variant="contained"
                startIcon={<LogoutIcon />}
              >
                Выйти
              </Button>
            </AccountPopoverFooter>
          </Stack>
        )}
      </>
    )
  }

  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <AppProvider
        theme={theme}
        navigation={navigation}
        branding={branding}
        session={session}
        authentication={authentication}
      >
        <ThemeProvider theme={theme}>
          <NotificationsProvider
            slotProps={{
              snackbar: {
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
              },
            }}
          >
            <DashboardLayout
              slots={{
                toolbarActions: ToolbarActions,
                toolbarAccount: ConditionalAccount,
                sidebarFooter: SidebarFooter,
              }}
            >
              <PageContainer breadcrumbs={[]} title="">
                {isNavigating ? <SkeletonContent /> : <Outlet />}
              </PageContainer>

              <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </DashboardLayout>
          </NotificationsProvider>
        </ThemeProvider>
      </AppProvider>
    </QueryParamProvider>
  )
}

export default Layout
